import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, X, CheckCircle2, ArrowRight, SkipForward } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

const STEP_ROUTES = {
  company_profile: "/settings",
  users: "/settings",
  insurance_companies: "/master/insurance",
  body_shops: "/master/body-shops",
  rate_table: "/master/rate-tables",
  first_customer: "/master/customers",
  integrations: "/settings",
};

export default function SetupWizardBanner() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem("setup_banner_dismissed") === "1");

  useEffect(() => {
    api.get("/setup/status").then((r) => setStatus(r.data)).catch(() => {});
  }, []);

  if (!status || status.complete || dismissed) return null;
  const next = status.pending_steps[0];
  if (!next) return null;

  const skip = async (key) => {
    const r = await api.post(`/setup/skip/${key}`);
    setStatus(r.data);
  };

  return (
    <div
      data-testid="setup-wizard-banner"
      className="mb-6 rounded-2xl border border-amber-500/30 bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-transparent p-5 relative overflow-hidden"
    >
      <div className="absolute -right-12 -top-12 w-48 h-48 rounded-full bg-amber-500/10 blur-3xl" />
      <div className="relative flex items-start gap-4">
        <div className="w-10 h-10 rounded-xl bg-amber-500/20 border border-amber-500/30 flex items-center justify-center shrink-0">
          <Sparkles className="w-5 h-5 text-amber-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-amber-300">Setup Wizard · {status.progress_pct}% complete</div>
            <div className="flex gap-1">
              {status.steps.map((s) => (
                <div
                  key={s.key}
                  className={`w-6 h-1.5 rounded-full ${
                    s.complete ? "bg-emerald-400" : s.skipped ? "bg-slate-600" : "bg-amber-500/40"
                  }`}
                  title={s.title}
                />
              ))}
            </div>
          </div>
          <div className="text-base font-semibold text-slate-100">Next: {next.title}</div>
          <div className="text-xs text-slate-400 mt-0.5">{next.description}</div>
          <div className="flex gap-2 mt-3">
            <Button
              data-testid="wizard-go-btn"
              size="sm"
              onClick={() => navigate(STEP_ROUTES[next.key] || "/settings")}
              className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
            >
              Take me there <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </Button>
            {next.key !== "company_profile" && (
              <Button
                data-testid="wizard-skip-btn"
                size="sm"
                variant="outline"
                onClick={() => skip(next.key)}
                className="border-slate-700 bg-slate-900/40 text-slate-300 hover:text-slate-100"
              >
                <SkipForward className="w-3.5 h-3.5 mr-1" /> Skip for now
              </Button>
            )}
            <Button
              data-testid="wizard-dismiss-btn"
              size="sm"
              variant="ghost"
              onClick={() => { sessionStorage.setItem("setup_banner_dismissed", "1"); setDismissed(true); }}
              className="text-slate-500 hover:text-slate-300"
            >
              Hide for this session
            </Button>
          </div>
        </div>
        <button onClick={() => { sessionStorage.setItem("setup_banner_dismissed", "1"); setDismissed(true); }} className="text-slate-500 hover:text-slate-300">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
